import { useEffect, useState, useCallback, useRef } from 'react';
import { listAssignments, listAccounts, updateAssignment, markDone, generateSeo, getCode, setCode, clearCode } from '../api/lionjob';
import { processTask, selectTags, competitorColor } from '../lib/teeyoung.js';
import LionjobPager from '../components/LionjobPager';

const STATUSES = ['pending', 'doing', 'done'];

// Lionjob (CHỈ Giám đốc + mã code). Sinh title/13 tag/description theo công thức Teeyoung,
// ghi ngược về DB tách riêng. Style scoped .mx-* dùng biến theme funytask.
export default function LionjobPage() {
  const [unlocked, setUnlocked] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [lockErr, setLockErr] = useState('');
  const [unlocking, setUnlocking] = useState(false);

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('pending'); // 'all' | 'pending' | 'doing' | 'done'
  const [accountFilter, setAccountFilter] = useState('all'); // lọc theo tên shop (account)
  const [accounts, setAccounts] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [gen, setGen] = useState(null);
  const [yt, setYt] = useState({});
  const [msg, setMsg] = useState('');
  const [extReady, setExtReady] = useState(false); // extension 🦁 đã cài & nhận diện được
  const [scanning, setScanning] = useState(false);
  const [claudeLoading, setClaudeLoading] = useState(false);

  const load = useCallback(async () => {
    const params = { page, limit: 20, dateField: 'created_at' };
    if (statusFilter !== 'all') params.status = statusFilter;
    if (accountFilter !== 'all') params.account = accountFilter;
    try {
      const data = await listAssignments(params);
      setRows(data.rows);
      setMeta({ page: data.page, totalPages: data.totalPages, total: data.total });
      setUnlocked(true);
    } catch (e) {
      const m = e.response?.data?.message || e.message || 'Không kết nối được backend';
      if (e.response?.status === 403) { clearCode(); setUnlocked(false); }
      setLockErr(m); setMsg('Lỗi: ' + m);
    }
  }, [page, statusFilter, accountFilter]);

  // Khi đã mở khóa (hoặc đổi trang/filter) thì tải danh sách.
  useEffect(() => { if (unlocked) load(); }, [load, unlocked]);
  // Nạp danh sách shop (account) để lọc, sau khi mở khóa.
  useEffect(() => { if (unlocked) listAccounts().then(setAccounts).catch(() => {}); }, [unlocked]);
  // Vào trang: nếu tab còn giữ mã (chưa đóng) thì thử tải luôn.
  useEffect(() => { if (getCode()) load(); /* eslint-disable-next-line */ }, []);

  // Cầu nối extension 🦁: nhận diện + nhận kết quả quét, rồi reload danh sách.
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);
  useEffect(() => {
    const onMsg = (ev) => {
      if (ev.source !== window || !ev.data || ev.data.source !== 'lionjob-ext') return;
      if (ev.data.type === 'ready') setExtReady(true);
      if (ev.data.type === 'scan-result') {
        setScanning(false);
        const r = ev.data.resp;
        if (r && r.ok) {
          setMsg(`✓ Quét ${r.scanned ?? r.received} từ Matrixty · mới ${r.inserted}, cập nhật ${r.updated}`);
          loadRef.current && loadRef.current();
        } else {
          setMsg('✗ ' + ((r && r.error) || 'Quét lỗi'));
        }
      }
    };
    window.addEventListener('message', onMsg);
    window.postMessage({ source: 'lionjob-page', type: 'ping' }, '*'); // hỏi xem có extension không
    return () => window.removeEventListener('message', onMsg);
  }, []);

  function scanFromMatrixty() {
    if (!extReady) { setMsg('Chưa thấy extension 🦁 — cài extension rồi mở tab matrixty.com.'); return; }
    setScanning(true);
    setMsg('Đang quét Listing Task từ Matrixty…');
    window.postMessage({ source: 'lionjob-page', type: 'scan', kind: 'assignment' }, '*');
    // Phòng khi extension không phản hồi.
    setTimeout(() => setScanning((s) => { if (s) setMsg('Quét quá lâu / không phản hồi — kiểm tra tab matrixty.com.'); return false; }), 45000);
  }

  async function unlock(e) {
    e?.preventDefault();
    const c = codeInput.trim();
    if (!c) { setLockErr('Nhập mã code'); return; }
    setLockErr(''); setCode(c); setUnlocking(true);
    try { await load(); } finally { setUnlocking(false); }
  }
  function lock() { clearCode(); setUnlocked(false); setCodeInput(''); setActiveId(null); setDraft(null); }

  const active = rows.find((r) => r.id === activeId) || null;

  function select(row) {
    setActiveId(row.id); setMsg('');
    setDraft({ title: row.title || '', tag: row.tag || '', description: row.description || '', status: row.status, publishLink: row.publishLink || '' });
    setGen(null);
  }
  // Tự điền cả 3 ô (title = gợi ý 1, tags, description) khi vừa sinh xong.
  function fillDraftFromGen(g) {
    setDraft((d) => ({ ...d, title: (g.titles && g.titles[0]) || '', tag: (g.tags || []).join(', '), description: g.description || '' }));
  }
  function runGenerate() {
    if (!active) return;
    const p = processTask({ id: active.id, status: 'pending', scrapedProduct: active.scrapedProduct });
    setGen(p); fillDraftFromGen(p);
  }
  async function runClaude() {
    if (!active) return;
    setClaudeLoading(true); setMsg('Đang gọi Claude…');
    try {
      const out = await generateSeo(active.id);
      const g = { titles: out.titles, tags: out.tags, tagCandidates: out.tags, description: out.description, ytrendsReady: false, copyrightRisk: out.copyrightRisk, _ctx: {} };
      setGen(g);
      // Điền 3 ô + TỰ LƯU ngay vào DB (giữ nguyên status) để không mất khi rời màn / reload.
      const nd = { title: (g.titles && g.titles[0]) || '', tag: (g.tags || []).join(', '), description: g.description || '' };
      setDraft((d) => ({ ...d, ...nd }));
      const updated = await updateAssignment(active.id, nd);
      setRows((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
      setMsg('✓ Đã sinh bằng Claude + TỰ LƯU. Muốn làm lại thì bấm "Sinh bằng Claude" lần nữa.');
    } catch (e) {
      setMsg('Claude lỗi: ' + (e.response?.data?.message || e.message));
    } finally { setClaudeLoading(false); }
  }
  function applyGen(field, value) { setDraft((d) => ({ ...d, [field]: value })); }
  function copyText(t) { navigator.clipboard.writeText(String(t || '')); setMsg('✓ Đã copy: ' + t); }

  function importYtrends(ev) {
    const f = ev.target.files[0]; if (!f) return;
    const r = new FileReader();
    r.onload = () => {
      try {
        const d = JSON.parse(r.result); delete d._note;
        const map = {};
        if (Array.isArray(d)) d.forEach((m) => { const k = (m.keyword || m.stats?.keyword || '').toLowerCase().trim(); if (k) map[k] = m.stats || m; });
        else Object.entries(d).forEach(([k, v]) => { map[k.toLowerCase().trim()] = v; });
        setYt(map);
        if (gen) {
          const byTag = {}; gen.tagCandidates.forEach((t) => { if (map[t]) byTag[t] = map[t]; });
          const tags = selectTags(gen.tagCandidates, Object.keys(byTag).length ? byTag : null, gen._ctx);
          setGen({ ...gen, tags, ytrendsReady: !!Object.keys(byTag).length });
        }
        setMsg('Đã nạp ' + Object.keys(map).length + ' metrics Ytrends');
      } catch (e) { setMsg('File Ytrends lỗi: ' + e.message); }
    };
    r.readAsText(f); ev.target.value = '';
  }

  async function save() {
    if (!active) return;
    try {
      setMsg('Đang lưu…');
      const updated = await updateAssignment(active.id, draft);
      setRows((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
      setMsg('✓ Đã lưu vào DB Lionjob');
      // Nếu đang lọc theo 1 trạng thái mà item vừa đổi sang trạng thái khác -> tải lại để rớt khỏi danh sách.
      if (statusFilter !== 'all' && updated.status !== statusFilter) load();
    } catch (e) { setMsg('Lưu lỗi: ' + (e.response?.data?.message || e.message)); }
  }
  async function doDone() {
    if (!active) return;
    const updated = await markDone(active.id);
    setDraft((d) => ({ ...d, status: 'done' }));
    setRows((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
    setMsg('✓ Đã đánh dấu Done');
    if (statusFilter !== 'all' && statusFilter !== 'done') load();
  }

  // Màn khóa: chưa mở khóa thì bắt buộc nhập mã code trước.
  if (!unlocked) {
    return (
      <div className="mx-root">
        <style>{MX_CSS}</style>
        <div className="mx-lock">
          <div className="mx-lockCard">
            <div className="mx-lockIco">🦁</div>
            <h2>Lionjob</h2>
            <p className="mx-lockSub">Khu vực chỉ dành cho Giám đốc. Nhập mã code để mở.</p>
            <form onSubmit={unlock}>
              <input className="mx-lockInput" type="password" autoFocus placeholder="Mã code" disabled={unlocking}
                value={codeInput} onChange={(e) => setCodeInput(e.target.value)} />
              <button className="mx-btn primary" type="submit" disabled={unlocking} style={{ width: '100%', marginTop: 10 }}>
                {unlocking ? '⏳ Đang kết nối…' : 'Mở khóa'}</button>
            </form>
            {lockErr && <div className="mx-lockErr">⚠ {lockErr}</div>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-root">
      <style>{MX_CSS}</style>
      <div className="mx-head">
        <div>
          <h2>Lionjob <span className="mx-tag">Giám đốc</span></h2>
          <div className="mx-sub">{msg || `${meta.total} assignment · trang ${meta.page}/${meta.totalPages}`}</div>
        </div>
        <div className="mx-spacer" />
        <button className="mx-btn primary" onClick={scanFromMatrixty} disabled={scanning || !extReady}
          title={extReady ? 'Quét Listing Task từ matrixty.com (/scraped-product-assignments)' : 'Cần cài extension 🦁'}>
          {scanning ? '⏳ Đang quét…' : '🦁 Quét Listing Task'}
        </button>
        <select className="mx-select" value={accountFilter} onChange={(e) => { setAccountFilter(e.target.value); setPage(1); }} title="Lọc theo shop (account)">
          <option value="all">Tất cả shop</option>
          {accounts.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <div className="mx-seg">
          {[['all', 'Tất cả'], ['pending', 'Pending'], ['doing', 'Doing'], ['done', 'Done']].map(([v, label]) => (
            <button key={v} className={'mx-segbtn' + (statusFilter === v ? ' active' : '')}
              onClick={() => { setStatusFilter(v); setPage(1); }}>{label}</button>
          ))}
        </div>
        <button className="mx-btn" onClick={lock} title="Khóa lại (xóa mã khỏi tab)">🔒 Khóa</button>
      </div>

      <div className="mx-wrap">
        <aside className="mx-list">
          {rows.length === 0 && <div className="mx-empty">Không có task</div>}
          {rows.map((r) => (
            <div key={r.id} className={'mx-item' + (r.id === activeId ? ' active' : '')} onClick={() => select(r)}>
              <div className="mx-it">{r.title || r.scrapedProduct.productTitle || '(chưa có title)'}</div>
              <div className="mx-im">{r.scrapedProduct.sku} · {r.scrapedProduct.website}
                <span className={'mx-badge ' + r.status}>{r.status}</span></div>
              {r.account?.shopName && <div className="mx-shop">🏬 {r.account.shopName}</div>}
            </div>
          ))}
        </aside>

        <main className="mx-detail">
          {!active && <div className="mx-empty">Chọn một task để sinh SEO Teeyoung.</div>}
          {active && draft && (
            <>
              <div className="mx-src">
                {active.scrapedProduct.productImage && <img src={active.scrapedProduct.productImage} alt="" />}
                <div>
                  <div className="mx-srcTitle">{active.scrapedProduct.productTitle}</div>
                  <div className="mx-srcMeta">{active.scrapedProduct.website} · {active.scrapedProduct.variant}{active.account.shopName ? ' · shop ' + active.account.shopName : ''}</div>
                  <div className="mx-sku">SKU: <b>{active.scrapedProduct.sku}</b>
                    <button className="mx-btn sm" onClick={() => copyText(active.scrapedProduct.sku)} title="Copy SKU">⧉ copy</button></div>
                </div>
              </div>

              <div className="mx-actions">
                <button className="mx-btn primary" onClick={runGenerate}>✦ Sinh bằng Teeyoung</button>
                <button className="mx-btn" onClick={runClaude} disabled={claudeLoading}
                  title="Sinh bằng Claude Opus 4.8 (cần ANTHROPIC_API_KEY ở backend)">
                  {claudeLoading ? '⏳ Claude…' : '✨ Sinh bằng Claude'}</button>
                <label className="mx-btn">Import Ytrends<input type="file" accept="application/json" onChange={importYtrends} style={{ display: 'none' }} /></label>
                {gen?.copyrightRisk && <span className="mx-risk">⚠ Rủi ro bản quyền (brand/IP)</span>}
              </div>

              {gen && (
                <div className="mx-gen">
                  <div className="mx-genLabel">3 title gợi ý {gen.ytrendsReady ? <span className="mx-badge yt">Ytrends ✓</span> : null}</div>
                  {gen.titles.map((t, i) => (
                    <div className="mx-genRow" key={i}>
                      <span className="mx-genText">{t} <span className="mx-cnt">{t.length}c/{t.trim().split(/\s+/).length}w</span></span>
                      <button className="mx-btn sm" onClick={() => applyGen('title', t)}>Dùng</button>
                    </div>
                  ))}
                  <div className="mx-genLabel">13 tag</div>
                  <div className="mx-genRow">
                    <span className="mx-genText mono">{gen.tags.join(', ')}</span>
                    <button className="mx-btn sm" onClick={() => applyGen('tag', gen.tags.join(', '))}>Dùng</button>
                  </div>
                  <div className="mx-chips">
                    {gen.tags.map((t) => <span key={t} className="mx-chip"><i className={'mx-dot ' + (yt[t] ? competitorColor(yt[t]) : 'unknown')} />{t}</span>)}
                  </div>
                  <div className="mx-genRow" style={{ marginTop: 8 }}>
                    <span className="mx-genText">Description ({gen.description.length} ký tự)</span>
                    <button className="mx-btn sm" onClick={() => applyGen('description', gen.description)}>Dùng</button>
                  </div>
                </div>
              )}

              <div className="mx-form">
                <label>Title</label>
                <textarea rows={2} value={draft.title} onChange={(e) => applyGen('title', e.target.value)} />
                <label>Tags (13, ngăn cách phẩy)</label>
                <textarea rows={2} value={draft.tag} onChange={(e) => applyGen('tag', e.target.value)} />
                <label>Description</label>
                <textarea rows={8} value={draft.description} onChange={(e) => applyGen('description', e.target.value)} />
                <div className="mx-row2">
                  <div><label>Status</label>
                    <select value={draft.status} onChange={(e) => applyGen('status', e.target.value)}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select></div>
                  <div style={{ flex: 1 }}><label>Publish link</label>
                    <input value={draft.publishLink} onChange={(e) => applyGen('publishLink', e.target.value)} placeholder="https://etsy.com/listing/…" /></div>
                </div>
                <div className="mx-actions">
                  <button className="mx-btn primary" onClick={save}>💾 Lưu vào DB Lionjob</button>
                  <button className="mx-btn ok" onClick={doDone} disabled={draft.status === 'done'}>Mark Done</button>
                </div>
              </div>
            </>
          )}
        </main>
      </div>

      <LionjobPager page={meta.page} totalPages={meta.totalPages} onGo={(p) => setPage(p)} />
    </div>
  );
}

const MX_CSS = `
.mx-root{color:var(--text)}
.mx-head{display:flex;gap:10px;align-items:center;margin-bottom:14px}
.mx-head h2{margin:0;font-size:18px}
.mx-tag{font-size:11px;color:var(--accent);border:1px solid var(--accent);border-radius:6px;padding:1px 6px;margin-left:6px;vertical-align:middle}
.mx-sub{color:var(--muted);font-size:12px;margin-top:2px}
.mx-spacer{flex:1}
.mx-chk{font-size:12px;color:var(--muted);display:flex;gap:6px;align-items:center}
.mx-select{background:var(--input);color:var(--text);border:1px solid var(--border);border-radius:10px;padding:7px 10px;font-size:12px;max-width:170px}
.mx-shop{color:var(--accent);font-size:11px;margin-top:2px}
.mx-seg{display:inline-flex;border:1px solid var(--border);border-radius:10px;overflow:hidden;background:var(--panel)}
.mx-segbtn{border:0;background:transparent;color:var(--muted);padding:6px 12px;font-size:12px;cursor:pointer;border-radius:0}
.mx-segbtn+.mx-segbtn{border-left:1px solid var(--border)}
.mx-segbtn:hover{color:var(--text)}
.mx-segbtn.active{background:var(--accent);color:#fff}
.mx-btn{background:var(--panel);color:var(--text);border:1px solid var(--border);padding:7px 12px;border-radius:10px;cursor:pointer;font-size:13px}
.mx-btn.sm{padding:4px 10px;font-size:12px}
.mx-btn:hover:not(:disabled){border-color:var(--accent)}
.mx-btn:disabled{opacity:.45;cursor:not-allowed}
.mx-btn.primary{background:var(--accent);border-color:var(--accent);color:#fff}
.mx-btn.ok{border-color:var(--ok);color:var(--ok)}
.mx-wrap{display:grid;grid-template-columns:300px 1fr;gap:14px;min-height:60vh}
.mx-list{border:1px solid var(--border);border-radius:var(--radius);overflow:auto;max-height:74vh;background:var(--panel)}
.mx-item{padding:10px 12px;border-bottom:1px solid var(--line);cursor:pointer}
.mx-item:hover{background:var(--input)}
.mx-item.active{background:var(--input);border-left:2px solid var(--accent)}
.mx-it{font-weight:600;font-size:13px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.mx-im{color:var(--muted);font-size:11px;margin-top:2px}
.mx-badge{display:inline-block;padding:1px 7px;border-radius:20px;font-size:11px;border:1px solid var(--border);margin-left:6px}
.mx-badge.pending{color:var(--warn);border-color:var(--warn)}
.mx-badge.doing{color:var(--info);border-color:var(--info)}
.mx-badge.done{color:var(--ok);border-color:var(--ok)}
.mx-badge.yt{color:var(--accent);border-color:var(--accent)}
.mx-detail{border:1px solid var(--border);border-radius:var(--radius);padding:16px 18px;background:var(--panel);overflow:auto;max-height:74vh}
.mx-empty{padding:50px;text-align:center;color:var(--muted)}
.mx-src{display:flex;gap:12px;align-items:center;margin-bottom:12px}
.mx-src img{width:56px;height:56px;border-radius:8px;object-fit:cover;border:1px solid var(--border)}
.mx-srcTitle{font-weight:600}
.mx-srcMeta{color:var(--muted);font-size:12px;margin-top:2px}
.mx-sku{font-size:12px;color:var(--muted);margin-top:4px;font-family:var(--font-mono);display:flex;align-items:center;gap:8px}
.mx-sku b{color:var(--text);letter-spacing:.5px}
.mx-actions{display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin:12px 0}
.mx-risk{color:var(--warn);font-size:12px}
.mx-gen{background:var(--input);border:1px solid var(--border);border-radius:10px;padding:12px;margin-bottom:14px}
.mx-genLabel{color:var(--muted);font-size:11px;text-transform:uppercase;letter-spacing:.5px;margin:8px 0 4px}
.mx-genLabel:first-child{margin-top:0}
.mx-genRow{display:flex;gap:8px;align-items:flex-start;margin-bottom:4px}
.mx-genText{flex:1;font-size:13px}
.mx-genText.mono{font-family:var(--font-mono);font-size:12px}
.mx-cnt{color:var(--muted);font-size:11px;font-family:var(--font-mono)}
.mx-chips{display:flex;flex-wrap:wrap;gap:6px;margin-top:6px}
.mx-chip{font-family:var(--font-mono);font-size:11px;padding:2px 8px;border-radius:20px;border:1px solid var(--border);display:inline-flex;gap:6px;align-items:center}
.mx-dot{width:8px;height:8px;border-radius:50%;background:var(--faint)}
.mx-dot.green{background:var(--ok)}.mx-dot.yellow{background:var(--warn-fill)}.mx-dot.red{background:var(--danger)}.mx-dot.unknown{background:var(--faint)}
.mx-form label{display:block;color:var(--muted);font-size:12px;text-transform:uppercase;letter-spacing:.5px;margin:12px 0 4px}
.mx-form textarea,.mx-form input,.mx-form select{width:100%;background:var(--input);color:var(--text);border:1px solid var(--border);border-radius:10px;padding:9px 11px;font-size:13px;font-family:inherit;resize:vertical}
.mx-form textarea{font-family:var(--font-mono);font-size:12px}
.mx-row2{display:flex;gap:12px;align-items:flex-end}
.mx-lock{display:flex;justify-content:center;padding:60px 20px}
.mx-lockCard{background:var(--panel);border:1px solid var(--border);border-radius:var(--radius);padding:28px 26px;width:340px;text-align:center}
.mx-lockIco{font-size:38px;line-height:1}
.mx-lockCard h2{margin:8px 0 4px}
.mx-lockSub{color:var(--muted);font-size:13px;margin:0 0 16px}
.mx-lockInput{width:100%;background:var(--input);color:var(--text);border:1px solid var(--border);border-radius:10px;padding:10px 12px;font-size:14px;text-align:center;letter-spacing:2px}
.mx-lockErr{color:var(--danger);font-size:12px;margin-top:10px}
`;
